import { MouseEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Tooltip } from "react-tooltip";
import { Button, Field, Input, Label } from "@headlessui/react";
import { ArrowPathIcon, ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { handleEnterSpaceKey } from "../../app/keyboardUtils";
import { isHigherThanDistrictAdmin } from "../../app/userSlice";
import { useAppSelector } from "../../app/hooks";
import { isMissingRequiredValue } from "../../app/requiredFields";
import { selectionChanged, textChanged } from "../../app/formChanges";
import ButtonWithConfirm from "../../components/ButtonWithConfirm";
import { ClassLinkOrCleverIcon } from "../../components/ClassLinkOrCleverIcon";
import ModalFrame from "../../components/ModalFrame";
import RequiredField from "../../components/RequiredField";
import { SimpleOption } from "../../components/input/SimpleListbox";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import Select from "../../components/input/Select";
import SearchableSelect from "../../components/input/SearchableSelect";
import {
	_defaultCountry,
	useGetCountriesQuery,
	useLazyGetZonesQuery,
} from "../countries/countriesApiSlice";
import { useGetDistrictsQuery } from "../districts/districtsApiSlice";
import SchoolOutsideSiteSettingsForm from "./schoolSettings/SchoolOutsideSiteSettingsForm";
import {
	SchoolEditModel,
	SchoolSpaViewModel,
	useAddSchoolMutation,
	useAddUserToSchoolMutation,
	useEditSchoolMutation,
	useGenerateReferralCodeMutation,
} from "./schoolsApiSlice";

interface Props {
	schoolInfo: SchoolSpaViewModel;
	addingNew: boolean;
	userIdToAdd?: number;
	returnTo?: string;
	saveButtonText?: string;
	setIsDialogOpen?: (isOpen: boolean) => void;
	setSchoolInfo?: (schoolInfo: SchoolSpaViewModel) => void;
}

export default function SchoolInfoSection(props: Props) {
	const { role } = useAppSelector((state) => state.user);
	const { config } = useAppSelector((state) => state.site);

	const [firstLoad, setFirstLoad] = useState(true);
	const [saveError, setSaveError] = useState("");
	const [disableSave, setDisableSave] = useState(false);

	const [schoolName, setSchoolName] = useState(props.schoolInfo.name ?? "");
	const [selectedDistrictId, setSelectedDistrictId] = useState(props.schoolInfo.district?.id);
	const [outsideSiteId, setOutsideSiteId] = useState(
		props.schoolInfo.showCleverID
			? (props.schoolInfo.cleverID ?? "")
			: (props.schoolInfo.classLinkID ?? "")
	);
	const [externalId, setExternalId] = useState(props.schoolInfo.externalID ?? "");
	const [referralCode, setReferralCode] = useState(props.schoolInfo.referralCode ?? "");

	const [streetAddress, setStreetAddress] = useState(props.schoolInfo.address ?? "");
	const [streetAddress2, setStreetAddress2] = useState(props.schoolInfo.address2 ?? "");
	const [city, setCity] = useState(props.schoolInfo.city ?? "");
	const [selectedStateId, setSelectedStateId] = useState(props.schoolInfo.zoneId);
	const [selectedCountryId, setSelectedCountryId] = useState(props.schoolInfo.countryId);
	const [zip, setZip] = useState(props.schoolInfo.zipCode ?? "");
	const [contactName, setContactName] = useState(props.schoolInfo.contactName ?? "");
	const [contactEmail, setContactEmail] = useState(props.schoolInfo.contactEmail ?? "");
	const [contactPhone, setContactPhone] = useState(props.schoolInfo.contactPhone ?? "");

	const [districtOptions, setDistrictOptions] = useState<SimpleOption[]>([]);
	const [stateOptions, setStateOptions] = useState<SimpleOption[]>([]);
	const [countryOptions, setCountryOptions] = useState<SimpleOption[]>([]);

	const [showAddressSection, setShowAddressSection] = useState(props.addingNew ? true : false);
	const [isOutsideSettingsFormOpen, setIsOutsideSettingsFormOpen] = useState(false);
	const showOutsideSite =
		!props.addingNew && (props.schoolInfo.showCleverID || props.schoolInfo.showClassLinkID);

	const getDistrictsResult = useGetDistrictsQuery();
	const districts = getDistrictsResult.data;

	const getCountriesResult = useGetCountriesQuery();
	const countries = getCountriesResult.data;

	const [callGetZonesForCountry, callGetZonesForCountryResult] = useLazyGetZonesQuery();

	const [addSchool] = useAddSchoolMutation();
	const [editSchool] = useEditSchoolMutation();
	const [generateReferralCode] = useGenerateReferralCodeMutation();
	const [addUserToSchool] = useAddUserToSchoolMutation();

	const navigate = useNavigate();

	function toggleShowAddressSection() {
		setShowAddressSection(!showAddressSection);
	}

	useEffect(() => {
		if (districts) {
			const options = districts.map((d) => ({ value: d.id, label: d.name }) as SimpleOption);
			if (isHigherThanDistrictAdmin(role!)) {
				options.unshift({ value: "", label: "None" } as SimpleOption);
			}

			setDistrictOptions(options);
		}
	}, [districts]);

	useEffect(() => {
		if (!countries) return;

		setCountryOptions(countries.map((c) => ({ value: c.id, label: c.name }) as SimpleOption));

		// The country defaults to the United States.  This is display only: nothing stores a
		// country on its own, so defaulting it can't give the school an address it never had.
		// The state is the part that persists, and that one stays blank until someone picks it.
		if (!selectedCountryId) {
			setSelectedCountryId(countries.find((c) => c.name == _defaultCountry)?.id ?? 0);
		}
	}, [countries]);

	useEffect(() => {
		if (!selectedCountryId) {
			setStateOptions([]);
			return;
		}

		let cancelled = false;

		callGetZonesForCountry(selectedCountryId)
			.unwrap()
			.then((zones) => {
				if (cancelled) return;

				const options = (zones ?? []).map(
					(z) => ({ value: z.id, label: z.name, code: z.code }) as SimpleOption
				);
				setStateOptions(options);

				// Seed the state once, for a new school, and only when the site actually
				// configures a default.  Without one the state stays blank for the user to
				// pick, rather than landing on whichever state happens to sort first.
				if (props.addingNew && firstLoad && !selectedStateId && config?.defaultState) {
					const stateId = options.find((z) => z.label == config.defaultState)?.value;

					if (stateId != null) setSelectedStateId(Number(stateId));
				}

				setFirstLoad(false);
			});

		return () => {
			cancelled = true;
		};
	}, [selectedCountryId]);

	// The selected state belongs to the old country, so clear it instead of carrying it over.
	function handleCountryChange(countryId: number) {
		setSelectedCountryId(countryId);
		setSelectedStateId(undefined);
	}

	useEffect(() => {
		// A new school has to supply every required field.  An existing one that was created
		// before a field became required may keep it empty, so that the new rule doesn't lock
		// the school out of being edited -- the same exemption the server makes.
		const isMissing = (current?: string | number, whenLoaded?: string | number) =>
			props.addingNew ? !current : isMissingRequiredValue(current, whenLoaded);

		const hasEmptyRequiredFields =
			!schoolName ||
			isMissing(externalId, props.schoolInfo.externalID) ||
			isMissing(city, props.schoolInfo.city) ||
			isMissing(selectedStateId, props.schoolInfo.zoneId) ||
			isMissing(selectedCountryId, props.schoolInfo.countryId) ||
			isMissing(contactName, props.schoolInfo.contactName);

		// There is nothing to save until one of the fields this form owns differs from what was
		// loaded.  The country is display only and nothing stores it on its own, and both the
		// referral code and the Clever/ClassLink id save themselves, so none of those count.
		const hasChanges =
			props.addingNew ||
			textChanged(schoolName, props.schoolInfo.name) ||
			selectionChanged(selectedDistrictId, props.schoolInfo.district?.id) ||
			textChanged(externalId, props.schoolInfo.externalID) ||
			textChanged(streetAddress, props.schoolInfo.address) ||
			textChanged(streetAddress2, props.schoolInfo.address2) ||
			textChanged(city, props.schoolInfo.city) ||
			textChanged(zip, props.schoolInfo.zipCode) ||
			textChanged(contactName, props.schoolInfo.contactName) ||
			textChanged(contactEmail, props.schoolInfo.contactEmail) ||
			textChanged(contactPhone, props.schoolInfo.contactPhone) ||
			selectionChanged(selectedStateId, props.schoolInfo.zoneId);

		setDisableSave(hasEmptyRequiredFields || !hasChanges);
	}, [
		schoolName,
		selectedDistrictId,
		externalId,
		streetAddress,
		streetAddress2,
		city,
		selectedStateId,
		selectedCountryId,
		zip,
		contactName,
		contactEmail,
		contactPhone,
	]);

	function updateModel(): SchoolEditModel {
		const model: SchoolSpaViewModel = {
			...props.schoolInfo,
			name: schoolName,
			district: districts?.find((d) => d.id == selectedDistrictId),
			cleverID: props.schoolInfo.cleverID ?? "",
			classLinkID: props.schoolInfo.classLinkID ?? "",
			externalID: externalId,
			referralCode: referralCode,
			address: streetAddress,
			address2: streetAddress2,
			city: city,
			zoneId: selectedStateId,
			countryId: selectedCountryId,
			zipCode: zip,
			contactName: contactName,
			contactEmail: contactEmail,
			contactPhone: contactPhone,
		};
		return { id: props.schoolInfo.id, model: model };
	}

	function handleCancel() {
		props.addingNew && props.setIsDialogOpen
			? props.setIsDialogOpen(false)
			: navigate(props.returnTo ?? `/schools`);
	}

	function handleSave(e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) {
		e.preventDefault();
		const model = updateModel();

		if (model) {
			if (props.addingNew) {
				addSchool(model.model)
					.unwrap()
					.then((id) => {
						// If userIdToAdd is provided (from AddSchoolToUser), add the user to the new school
						if (props.userIdToAdd && id) {
							addUserToSchool({ schoolId: id, userId: props.userIdToAdd })
								.unwrap()
								.then(() => {
									if (props.setIsDialogOpen) props.setIsDialogOpen(false);
								});
						} else {
							if (props.setIsDialogOpen) props.setIsDialogOpen(false);
							navigate(props.returnTo ?? `/schools/edit?schoolId=${id}`);
						}
					})
					.catch((error) => {
						setSaveError(error.data);
					});
			} else {
				editSchool(model)
					.unwrap()
					.then(() => {
						navigate(props.returnTo ?? `/schools`);
					})
					.catch((error) => {
						setSaveError(error.data);
						scrollTo(0, 0);
					});
			}
		}
	}

	function handleOutsideSiteIdUpdate(id: string) {
		setOutsideSiteId(id);

		if (props.setSchoolInfo)
			props.setSchoolInfo({
				...props.schoolInfo,
				cleverID: props.schoolInfo.showCleverID ? id : props.schoolInfo.cleverID,
				classLinkID: props.schoolInfo.showClassLinkID ? id : props.schoolInfo.classLinkID,
			});
	}

	const handleOutsideSettingsFormClose = () => setIsOutsideSettingsFormOpen(false);

	function handleGenerateReferralCode() {
		generateReferralCode(props.schoolInfo.id)
			.unwrap()
			.then((code) => {
				setReferralCode(code);
				setSaveError("");
			})
			.catch((error) => {
				setSaveError(error.data);
			});
	}

	return (
		<section>
			{!config?.isUtahComposeSite && !props.addingNew && <hr className="my-6" />}
			<div className={`${props.addingNew ? "mt-2" : "mt-6"} flex flex-col gap-x-12 w-full`}>
				<div className="flex justify-between">
					<h2 className="mb-2">School Information</h2>
					<p className="text-xs leading-6 text-gray-600">
						<RequiredField className="!text-sm" /> - Denotes a required field
					</p>
				</div>
				<AlertMessage
					className="mb-2"
					type={AlertLevel.Error}
					message={saveError}
					showIcon={true}
					onClear={() => setSaveError("")}
				/>
				<div
					className={`${props.addingNew ? "gap-y-2" : "gap-y-3"} grid grid-cols-1 gap-x-6 sm:grid-cols-6`}>
					<Field className={`${props.addingNew ? "col-span-full" : "sm:col-span-3"}`}>
						<Label htmlFor="schoolName" className="font-medium text-sm">
							Name
							<RequiredField />
						</Label>
						<Input
							id="schoolName"
							name="schoolName"
							type="text"
							required
							maxLength={100}
							className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
							value={schoolName}
							onChange={(e) => setSchoolName(e.target.value)}
						/>
					</Field>
					<Field className="sm:col-span-3">
						<Select
							value={selectedDistrictId}
							setValue={(v) => setSelectedDistrictId(v as number)}
							label="District"
							requiredField={true}
							simpleListBoxId="districtDropdown"
							options={districtOptions}
							fieldClasses="md:pt-0"
							simpleListBoxClasses="w-full"
						/>
					</Field>
					{showOutsideSite && (
						<Field className="sm:col-span-2">
							<Label htmlFor="classlinkClever" className="font-medium text-sm">
								{props.schoolInfo.showCleverID ? "Clever" : "ClassLink"} ID
							</Label>
							<div className="flex mt-1">
								<Input
									id="classlinkClever"
									name="classlinkClever"
									type="text"
									readOnly
									className="rounded-none rounded-l-lg block w-full border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									value={outsideSiteId}
								/>
								<Button
									id="outside-site-form-button"
									className="rounded-lg rounded-l-none border-0 ring-1 ring-inset ring-gray-900/20 cursor-pointer flex justify-center overflow-hidden"
									onClick={() => setIsOutsideSettingsFormOpen(true)}>
									<ClassLinkOrCleverIcon
										iconClasses="h-9 w-10 !rounded-lg !rounded-l-none"
										isClassLinkEntity={props.schoolInfo.showClassLinkID}
										isCleverEntity={props.schoolInfo.showCleverID}
									/>
								</Button>
							</div>
							{isOutsideSettingsFormOpen && (
								<ModalFrame
									open={isOutsideSettingsFormOpen}
									title={`${props.schoolInfo.showCleverID ? "Clever Settings" : "ClassLink Settings"}`}
									onClose={handleOutsideSettingsFormClose}>
									<SchoolOutsideSiteSettingsForm
										schoolId={props.schoolInfo.id}
										isCleverForm={props.schoolInfo.showCleverID}
										onUpdateOutsideSiteId={handleOutsideSiteIdUpdate}
										onClose={handleOutsideSettingsFormClose}
									/>
								</ModalFrame>
							)}
						</Field>
					)}
					<Field className={`${showOutsideSite ? "sm:col-span-2" : "sm:col-span-3"}`}>
						<Label htmlFor="externalId" className="font-medium text-sm">
							NCES or External ID
							<RequiredField />
						</Label>
						<Input
							id="externalId"
							name="externalId"
							type="text"
							required
							maxLength={40}
							className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
							value={externalId}
							onChange={(e) => setExternalId(e.target.value)}
						/>
					</Field>
					{!props.addingNew && (
						<Field className={`${showOutsideSite ? "sm:col-span-2" : "sm:col-span-3"}`}>
							<Label htmlFor="referralCode" className="font-medium text-sm">
								Referral Code
							</Label>
							<div className="flex mt-1">
								<Input
									id="referralCode"
									name="referralCode"
									type="text"
									readOnly
									value={referralCode}
									className={`${role && isHigherThanDistrictAdmin(role) ? "rounded-none rounded-l-lg" : "rounded-lg"} block w-full border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600`}
								/>
								{role && isHigherThanDistrictAdmin(role) && (
									<>
										<ButtonWithConfirm
											tooltipId="referralCode-tooltip"
											ariaLabel="Generate New Referral Code"
											className="relative border border-gray-300 inline-flex items-center rounded-r-lg px-2 py-1 text-sm font-semibold text-gray-900 hover:bg-gray-50"
											message="Do you want to generate a new referral code for the school? This will overwrite any previous codes."
											confirmLabel="Yes"
											onConfirm={handleGenerateReferralCode}>
											<ArrowPathIcon className="size-5" />
										</ButtonWithConfirm>
										<Tooltip
											id="referralCode-tooltip"
											place="bottom-end"
											content="Generate New Referral Code"
											openEvents={{
												mouseenter: true,
												focus: false,
												click: false,
											}}
											closeEvents={{
												mouseleave: true,
												blur: true,
											}}
										/>
									</>
								)}
							</div>
						</Field>
					)}
				</div>
			</div>

			<div className="mt-2">
				<div
					className="flex items-center cursor-pointer"
					onClick={toggleShowAddressSection}>
					{showAddressSection ? (
						<ChevronDownIcon
							tabIndex={0}
							className="size-5 hover:cursor-pointer"
							onClick={toggleShowAddressSection}
							onKeyDown={(e) => handleEnterSpaceKey(e, toggleShowAddressSection)}
						/>
					) : (
						<ChevronRightIcon
							tabIndex={0}
							className="size-5 hover:cursor-pointer"
							onClick={toggleShowAddressSection}
							onKeyDown={(e) => handleEnterSpaceKey(e, toggleShowAddressSection)}
						/>
					)}
					<h3 className="text-base font-semibold text-gray-900 pt-2 pb-2 ml-4">
						Address and Contact Information
					</h3>
				</div>
				{showAddressSection && (
					<div className="flex gap-x-12 w-full">
						<div className="flex-col w-3/5">
							<h2 className="mb-2">Address</h2>
							<div
								className={`${props.addingNew ? "gap-y-2" : "gap-y-4"} grid grid-cols-1 gap-x-6 sm:grid-cols-9`}>
								<Field className="col-span-full">
									<Label htmlFor="line1" className="font-medium text-sm">
										Street Address
									</Label>
									<Input
										id="line1"
										name="line1"
										type="text"
										maxLength={150}
										className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										value={streetAddress}
										onChange={(e) => setStreetAddress(e.target.value)}
									/>
								</Field>
								<Field className="col-span-full">
									<Label htmlFor="line2" className="font-medium text-sm">
										Street Address Line 2
									</Label>
									<Input
										id="line2"
										name="line2"
										type="text"
										maxLength={150}
										className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										value={streetAddress2}
										onChange={(e) => setStreetAddress2(e.target.value)}
									/>
								</Field>
								<Field
									className={`${props.addingNew ? "col-span-4" : "col-span-full md:col-span-4 2xl:col-span-2"}`}>
									<Label htmlFor="city" className="font-medium text-sm">
										City
										<RequiredField />
									</Label>
									<Input
										id="city"
										name="city"
										type="text"
										required
										maxLength={35}
										className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										value={city}
										onChange={(e) => setCity(e.target.value)}
									/>
								</Field>

								<Field
									className={`${props.addingNew ? "col-span-5" : "col-span-full md:col-span-5 2xl:col-span-3"}`}>
									<SearchableSelect
										value={selectedStateId}
										setValue={(v) => setSelectedStateId(v as number)}
										label="State"
										requiredField={true}
										prompt="Select a state"
										id="stateDropdown"
										options={stateOptions}
									/>
								</Field>
								<Field
									className={`${props.addingNew ? "col-span-6" : "col-span-full md:col-span-6 2xl:col-span-3"}`}>
									<SearchableSelect
										value={selectedCountryId}
										setValue={(v) => handleCountryChange(v as number)}
										label="Country"
										requiredField={true}
										prompt="Select a country"
										id="countryDropdown"
										options={countryOptions}
									/>
								</Field>

								<Field
									className={`${props.addingNew ? "col-span-3" : "col-span-full md:col-span-3 2xl:col-span-1"}`}>
									<Label htmlFor="zip" className="font-medium text-sm">
										ZIP Code
									</Label>
									<Input
										id="zip"
										name="zip"
										type="text"
										maxLength={10}
										className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										value={zip}
										onChange={(e) => setZip(e.target.value)}
									/>
								</Field>
							</div>
						</div>
						<div className="flex-col w-2/5">
							<h2 className="mb-2">Contact Information</h2>
							<div
								className={`${props.addingNew ? "gap-y-2" : "gap-y-4"} grid grid-cols-1 gap-x-6 sm:grid-cols-6`}>
								<Field
									className={`${props.addingNew ? "col-span-full" : "col-span-5"}`}>
									<Label htmlFor="contactName" className="font-medium text-sm">
										Name
										<RequiredField />
									</Label>
									<Input
										id="contactName"
										name="contactName"
										type="text"
										required
										maxLength={60}
										className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										value={contactName}
										onChange={(e) => setContactName(e.target.value)}
									/>
								</Field>

								<Field
									className={`${props.addingNew ? "col-span-full" : "col-span-5"}`}>
									<Label htmlFor="email" className="font-medium text-sm">
										Email
									</Label>
									<Input
										id="email"
										name="email"
										type="text"
										maxLength={256}
										className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										value={contactEmail}
										onChange={(e) => setContactEmail(e.target.value)}
									/>
								</Field>

								<Field
									className={`${props.addingNew ? "col-span-full" : "col-span-4 lg:col-span-3"}`}>
									<Label htmlFor="phone" className="font-medium text-sm">
										Phone
									</Label>
									<Input
										id="phone"
										name="phone"
										type="text"
										maxLength={255}
										className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										value={contactPhone}
										onChange={(e) => setContactPhone(e.target.value)}
									/>
								</Field>
							</div>
						</div>
					</div>
				)}
			</div>
			<div
				className={`${props.addingNew ? "mt-3 grid grid-cols-2 gap-3" : "flex items-center justify-end gap-x-4"}`}>
				<Button
					type="button"
					id="cancel-button"
					className={`btn ${props.addingNew ? "btn-neutral" : "btn-naked"}`}
					onClick={handleCancel}>
					Cancel
				</Button>
				<Button
					type="submit"
					className="btn btn-primary"
					disabled={disableSave}
					onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleSave(e)}>
					{props.saveButtonText ?? "Save"}
				</Button>
			</div>
		</section>
	);
}
